import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { CheckCircle2, XCircle, Calendar as CalendarIcon, User, Bus, Ship, Sparkles, MapPin, Clock } from "lucide-react";

import { getTicketByToken } from "@/lib/tickets.functions";

export const Route = createFileRoute("/ticket/$token")({
  head: () => ({
    meta: [
      { title: "ticket — what" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TicketPage,
});

function formatDateLong(d: string) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateShort(d: string) {
  const [y, m, day] = d.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, day));
  return {
    day: String(day).padStart(2, "0"),
    month: date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }).toUpperCase(),
    year: String(y),
    weekday: date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" }).toUpperCase(),
  };
}

function operatorLabel(op: string | null) {
  if (op === "royalline") return "royalline.fi";
  if (op === "citytour") return "citytour.fi";
  if (op === "combo") return "royalline + citytour";
  return "what finland";
}

function OperatorIcon({ op, className = "size-4" }: { op: string | null; className?: string }) {
  if (op === "royalline") return <Bus className={className} />;
  if (op === "citytour") return <Ship className={className} />;
  return <Sparkles className={className} />;
}

function TicketPage() {
  const { token } = Route.useParams();
  const fetchTicket = useServerFn(getTicketByToken);
  const { data, isLoading } = useQuery({
    queryKey: ["ticket-public", token],
    queryFn: () => fetchTicket({ data: { token } }),
  });

  const [qrSvg, setQrSvg] = useState<string>("");

  useEffect(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (!url) return;
    QRCode.toString(url, { type: "svg", margin: 1, width: 280, errorCorrectionLevel: "M" })
      .then(setQrSvg)
      .catch(() => setQrSvg(""));
  }, [token]);

  if (isLoading) {
    return <div className="max-w-md mx-auto px-4 py-16 text-sm text-muted-foreground">loading ticket…</div>;
  }

  const ticket = data?.ticket;

  if (!ticket) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="rounded-3xl border-2 border-red-500 bg-red-50 p-8">
          <XCircle className="size-16 text-red-600 mx-auto" />
          <h1 className="mt-4 font-display text-2xl sm:text-3xl capitalize font-extrabold text-red-900">Invalid Ticket</h1>
          <p className="mt-2 text-sm text-red-700">this ticket doesn't exist or the link is broken.</p>
        </div>
      </div>
    );
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = ticket.valid_date === todayStr;
  const isFuture = ticket.valid_date > todayStr;
  const isUsed = !!ticket.used_at;
  const isValid = isToday && !isUsed;

  const dateParts = formatDateShort(ticket.valid_date);

  const status = isValid
    ? { label: "valid", sub: "admit holder", tone: "valid" as const, Icon: CheckCircle2 }
    : isUsed
      ? {
          label: "already used",
          sub: `scanned ${new Date(ticket.used_at!).toLocaleString("en-GB")}`,
          tone: "used" as const,
          Icon: XCircle,
        }
      : isFuture
        ? {
            label: "not yet",
            sub: `valid on ${formatDateLong(ticket.valid_date)}`,
            tone: "future" as const,
            Icon: CalendarIcon,
          }
        : {
            label: "expired",
            sub: `was for ${formatDateLong(ticket.valid_date)}`,
            tone: "expired" as const,
            Icon: XCircle,
          };

  const statusColor =
    status.tone === "valid"
      ? "bg-emerald-500"
      : status.tone === "used"
        ? "bg-amber-500"
        : status.tone === "future"
          ? "bg-[#0b1d3a]"
          : "bg-red-500";

  const statusText =
    status.tone === "valid"
      ? "text-emerald-50"
      : status.tone === "used"
        ? "text-amber-50"
        : status.tone === "future"
          ? "text-white"
          : "text-red-50";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100 px-4 py-6 md:py-12">
      <div className="max-w-md mx-auto">
        {/* Ticket */}
        <div className="relative drop-shadow-[0_20px_50px_rgba(11,29,58,0.18)]">
          {/* Status header / stub top */}
          <div className={`${statusColor} ${statusText} rounded-t-3xl px-6 pt-6 pb-8 relative overflow-hidden`}>
            <div className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, transparent 0 12px, rgba(255,255,255,0.4) 12px 13px)",
              }}
            />
            <div className="relative flex items-center justify-between">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.22em]">
                <OperatorIcon op={ticket.operator} className="size-3.5" />
                <span>{operatorLabel(ticket.operator)}</span>
              </div>
              <status.Icon className="size-6 opacity-90" />
            </div>
            <div className="relative mt-6">
              <div className="font-display text-5xl font-extrabold capitalize leading-[0.95] tracking-tight">
                {status.label}
              </div>
              <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.24em] opacity-90">
                {status.sub}
              </div>
            </div>
          </div>

          {/* Perforation */}
          <div className="relative bg-card h-6">
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100" />
            <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100" />
            <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-border" />
          </div>

          {/* Hero image */}
          {ticket.image_url && (
            <div className="bg-card overflow-hidden">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={ticket.image_url}
                  alt={ticket.product_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                {/* big date stamp */}
                <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2 flex items-center gap-3 shadow-lg">
                  <div className="text-center leading-none">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                      {dateParts.weekday}
                    </div>
                    <div className="font-display text-2xl font-extrabold text-[#0b1d3a] mt-0.5">
                      {dateParts.day}
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                      {dateParts.month} {dateParts.year}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="bg-card px-6 pt-5 pb-6">
            <h1 className="font-display text-[21px] sm:text-[26px] capitalize leading-[1.1] font-extrabold tracking-tight text-[#0b1d3a]">
              {ticket.product_name}
            </h1>
            {ticket.duration_label && (
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                <Clock className="size-3" /> {ticket.duration_label}
              </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-4">
              <Field label="holder" icon={<User className="size-3.5" />}>
                <span className="text-[#0b1d3a]">{ticket.holder_name}</span>
              </Field>
              <Field label="ticket id">
                <span className="font-mono tracking-[0.1em] text-[#0b1d3a]">{ticket.shortId}</span>
              </Field>
              <Field label="valid date" icon={<CalendarIcon className="size-3.5" />}>
                <span className="text-[#0b1d3a]">{formatDateLong(ticket.valid_date)}</span>
              </Field>
              <Field label="gate" icon={<MapPin className="size-3.5" />}>
                <span className="text-[#0b1d3a]">helsinki</span>
              </Field>
            </div>
          </div>

          {/* QR perforation */}
          <div className="relative bg-card h-6">
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100" />
            <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100" />
            <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-border" />
          </div>

          {/* QR stub */}
          <div className="bg-card rounded-b-3xl px-6 pt-8 pb-8 flex flex-col items-center">
            {qrSvg && (
              <div className="p-4 bg-white rounded-2xl border border-border shadow-sm">
                <div
                  className="w-[240px] h-[240px] [&_svg]:block [&_svg]:w-full [&_svg]:h-full"
                  dangerouslySetInnerHTML={{ __html: qrSvg }}
                  aria-label="ticket QR code"
                />
              </div>
            )}
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              scan at the gate
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          what finland · keep this page open at the gate
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="group relative rounded-2xl border border-border/70 bg-gradient-to-br from-slate-50/80 to-white p-3.5 transition-all hover:border-[#0b1d3a]/20 hover:shadow-sm">
      <div className="absolute left-0 top-3 bottom-3 w-0.5 rounded-r-full bg-[#0b1d3a]/15 group-hover:bg-[#0b1d3a]/40 transition-colors" />
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground">
        {icon ? <span className="text-[#0b1d3a]/60">{icon}</span> : null}
        {label}
      </div>
      <div className="mt-1.5 text-sm font-bold leading-tight">{children}</div>
    </div>
  );
}
